/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {Control, Controller, FieldErrors} from 'react-hook-form'
import clsx from 'clsx'
import {AntdDatePicker} from 'components/Ui/AntdPickerWrapper'
import dayjs, {Dayjs} from 'dayjs'
import {useTranslation} from 'react-i18next'
import {DATE_FORMATS} from 'constants/DateFormats'

import 'dayjs/locale/de'
import 'dayjs/locale/de-ch'
import 'dayjs/locale/en'
import 'dayjs/locale/nl'
import 'dayjs/locale/sk'
import 'dayjs/locale/hu'
import 'dayjs/locale/it'

interface UiTimePickerProps {
    control?: Control<any>
    name?: string
    label?: string
    errors?: FieldErrors
    error?: string
    className?: string
    containerClassName?: string
    required?: boolean
    disabled?: boolean
    placeholder?: string
    value?: Date | string | null
    onChange?: (date: Date | null) => void
    format?: string
    endIcon?: React.ReactNode
}

const UiTimePicker = (props: UiTimePickerProps) => {
    const { t } = useTranslation()
    const errorMessage = props.error || props.errors?.[props.name || '']?.message;


    const renderPicker = (field?: any) => {
        const value = field ? field.value : props.value
        const parsedDateValue = value ? dayjs(value) : null
        const dateValue = parsedDateValue?.isValid() ? parsedDateValue : null

        const handleChange = (date: Dayjs | null) => {
            const nativeDate = date ? date.toDate() : null
            if (field) {
                field.onChange(nativeDate)
            }
            props.onChange?.(nativeDate)
        }

        const hasError = !!(
            props.error || props.errors?.[props.name || '']?.message
        )

        return (
            <div
                className={clsx(
                    'flex items-center rounded-lg overflow-hidden border bg-white dark:bg-gray-950 transition-colors shadow-sm',
                    hasError
                        ? 'border-red-500 focus-within:ring-2 focus-within:ring-cta-light'
                        : 'border-gray-300 dark:border-gray-800 focus-within:border-cta-light focus-within:ring-2 focus-within:ring-cta-light',
                    props.disabled &&
                        'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900'
                )}
            >
                <AntdDatePicker
                    picker="time"
                    allowClear={!props.required}
                    className={clsx(
                        'w-full h-10 px-3 bg-transparent border-0 text-gray-900 dark:text-gray-100 focus:border-0 shadow-none'
                    )}
                    style={{ borderWidth: 0, width: '100%' }}
                    format={props.format || DATE_FORMATS.TIME}
                    suffixIcon={props.endIcon}
                    minuteStep={10}
                    hideDisabledOptions
                    disabledTime={() => ({ disabledHours: () => [0, 1, 2, 3, 4, 5] })}
                    value={dateValue}
                    onChange={handleChange}
                    disabled={props.disabled}
                    placeholder={props.placeholder || t('Select time')}
                    inputReadOnly={true}
                    showNow={false}
                    needConfirm={false}
                />
            </div>
        )
    }

    return (
        <div
            className={clsx(
                'UiTimePicker flex flex-col gap-1.5',
                props.className,
                props.containerClassName
            )}
        >
            {props.label && (
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {props.label}
                    {props.required && (
                        <span className="text-red-500 ml-0.5">*</span>
                    )}
                </label>
            )}

            <div className="relative w-full">
                {props.control && props.name ? (
                    <Controller
                        control={props.control}
                        name={props.name}
                        render={({ field }) => renderPicker(field)}
                    />
                ) : (
                    renderPicker()
                )}
            </div>

            {errorMessage && (
                <span className="text-red-500 text-xs mt-1">
                    {String(errorMessage)}
                </span>
            )}
        </div>
    )
}

export default UiTimePicker
